import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';
import InfoTooltip from '../../../utils/InfoTooltip';
import { syncCupcakeUnitConfigs } from '../utils/cupcakeOrderUtils';
import { isCupcakeProductPreset } from '../utils/orderFormLinks';

const cakeOptions = {
  "Sheet Cakes": [
    { label: "9x13 (1/4 sheet)", servings: 20, price: 70 },
    { label: "11x15 (1/3 sheet)", servings: 35, price: 88 },
    { label: "12x18 (1/2 sheet)", servings: 50, price: 108 },
    { label: "22x15 (2/3 sheet)", servings: 75, price: 136 },
    { label: "24x18 (full sheet)", servings: 100, price: 172 }
  ],
  "Round / Square Cakes": [
    { label: '6" double layer', servings: 8, price: 39 },
    { label: '8" single layer', servings: 10, price: 36 },
    { label: '8" double layer', servings: 20, price: 70 },
    { label: '10" double layer', servings: 35, price: 97 },
    { label: '12" double layer', servings: 50, price: 119 },
    { label: '14" double layer', servings: 75, price: 144 },
    { label: '16" double layer', servings: 100, price: 198 }
  ],
  "Oval / Heart Cakes": [
    { label: '6" double layer', servings: 8, price: 42 },
    { label: '9" single layer', servings: 10, price: 36.25 },
    { label: '9" double layer', servings: 20, price: 73.75 },
    { label: '12" double layer ', servings: 45, price: 100 },
    { label: '15" double layer', servings: 75, price: 136.25 }
  ]
};

const shapeMapping = {
  Round: "Round / Square Cakes",
  Square: "Round / Square Cakes",
  Oval: "Oval / Heart Cakes",
  Heart: "Oval / Heart Cakes",
  Sheet: "Sheet Cakes"
};

const CAKE_SHAPES = [
  { shape: 'Round', icon: '🎂', tip: '8–100 servings' },
  { shape: 'Square', icon: '🟦', tip: '8–100 servings' },
  { shape: 'Oval', icon: '⬭', tip: '10–75 servings' },
  { shape: 'Heart', icon: '❤️', tip: 'Romantic occasions' },
  { shape: 'Sheet', icon: '▭', tip: '20–100+ servings' },
];

const CUPCAKE_DOZEN_OPTIONS = Array.from({ length: 19 }, (_, i) => 1 + i * 0.5);
const CUPCAKE_DOZEN_VALUE_SET = new Set(CUPCAKE_DOZEN_OPTIONS.map((n) => String(n)));

function isAllowedCupcakeDozens(d) {
  if (!Number.isFinite(d) || d < 1 || d > 10) return false;
  return CUPCAKE_DOZEN_VALUE_SET.has(String(d));
}

const clearCupcakeFields = {
  cupcakeDozens: 0,
  cupcakeUnitConfigs: [],
  cupcakeConfigMode: 'uniform',
  cupcakeFlavorSelections: [],
  icingSelections: [],
  sugarSprinkleColors: [],
  cupcakeIcingTintEnabled: false,
  cupcakeIcingTintColor: '',
};

const StepThreeCakeDetails = ({ formData, setFormData, nextStep, prevStep }) => {
  const location = useLocation();
  const isCupcakeFlow = isCupcakeProductPreset(new URLSearchParams(location.search));
  const [selectedShape, setSelectedShape] = useState(
    () => (formData.cakeShape === 'Cupcakes' ? '' : formData.cakeShape || '')
  );
  const [error, setError] = useState('');
  const skipValidation = false;
  const cupcakeDozens = Number(formData.cupcakeDozens || 0);

  React.useEffect(() => {
    setSelectedShape(formData.cakeShape === 'Cupcakes' ? '' : formData.cakeShape || '');
  }, [formData.cakeShape]);

  React.useEffect(() => {
    setError('');
    if (isCupcakeFlow) {
      setFormData((prev) => ({
        ...(prev.cakeShape === 'Cupcakes'
          ? prev
          : {
              ...prev,
              cakeShape: 'Cupcakes',
              cakeSize: 'Cupcakes',
            }),
        cupcakeDozens: prev.cupcakeDozens || 0,
        servings: prev.cupcakeDozens ? String(Number(prev.cupcakeDozens) * 12) : '',
        ...(() => {
          const dozensNum = Number(prev.cupcakeDozens || 0);
          const cupcakeBase = dozensNum > 0 ? dozensNum * 30 : 0;
          return {
            baseCakePrice: cupcakeBase,
            price: cupcakeBase > 0 ? cupcakeBase.toFixed(2) : '',
          };
        })(),
      }));
    } else {
      setFormData((prev) => ({
        ...(prev.cakeShape === 'Cupcakes'
          ? {
              ...prev,
              cakeShape: '',
              cakeSize: '',
              baseCakePrice: '',
              price: '',
              servings: '',
              ...clearCupcakeFields,
            }
          : prev),
      }));
    }
  }, [isCupcakeFlow, setFormData]);

  return (
    <div className="space-y-8 px-2 sm:px-4 py-2 sm:py-4 max-w-3xl mx-auto">
      <h2 className="text-center text-xl sm:text-2xl md:text-3xl font-bold text-candy-text font-hobo">
        {isCupcakeFlow ? 'Cupcake Quantity' : 'Choose Cake Shape & Size'}
      </h2>

      <AnimatePresence mode="wait">
        {!isCupcakeFlow && (
          <motion.div
            key="cake"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            <motion.div>
              <label className="block font-semibold mb-2 text-gray-700">Choose cake shape</label>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 sm:gap-3">
                {CAKE_SHAPES.map(({ shape, icon, tip }) => (
                  <button
                    key={shape}
                    type="button"
                    className={`min-h-[5.5rem] sm:min-h-[6.5rem] flex flex-col items-center justify-center rounded-lg border p-2 text-center text-xs sm:text-sm font-medium transition-all ${
                      selectedShape === shape
                        ? 'bg-[#a62c2b] text-white border-[#a62c2b] shadow-md'
                        : 'bg-white text-gray-700 border-candy-border hover:border-[#a62c2b]'
                    }`}
                    onClick={() => {
                      setSelectedShape(shape);
                      setFormData((prev) => ({
                        ...prev,
                        cakeShape: shape,
                        cakeSize: '',
                        ...clearCupcakeFields,
                      }));
                    }}
                  >
                    <span className="text-2xl sm:text-3xl leading-none mb-1">{icon}</span>
                    <span className="font-semibold">{shape}</span>
                    <span className={`text-[10px] sm:text-[11px] leading-tight mt-0.5 ${selectedShape === shape ? 'text-gray-200' : 'text-gray-500'}`}>
                      {tip}
                    </span>
                  </button>
                ))}
              </div>
            </motion.div>

            {selectedShape && (
              <motion.div>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <label className="block font-semibold text-gray-700">Select size</label>
                  <InfoTooltip position="right" width="w-72">
                    <p className="text-[11px] leading-snug text-center">
                      <strong>Single layer:</strong> One cake layer with frosting on top.<br />
                      <strong>Double layer:</strong> Two stacked layers with filling or frosting in between.
                    </p>
                  </InfoTooltip>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  {cakeOptions[shapeMapping[selectedShape]]?.map(({ label, servings, price }) => {
                    const isSelected = formData.cakeSize === label;
                    return (
                      <label
                        key={label}
                        className={`cursor-pointer p-3 sm:p-4 rounded-lg border transition-all text-sm ${
                          isSelected
                            ? 'border-[#a62c2b] bg-red-50 shadow-md'
                            : 'border-candy-border bg-white hover:border-[#a62c2b]'
                        }`}
                      >
                        <input
                          type="radio"
                          name="cakeSize"
                          value={label}
                          checked={isSelected}
                          onChange={() => {
                            setFormData((prev) => ({
                              ...prev,
                              cakeSize: label,
                              price: price.toFixed(2),
                              baseCakePrice: price,
                              servings: String(servings),
                            }));
                          }}
                          className="hidden"
                        />
                        <div className="font-semibold text-gray-800">{label}</div>
                        <div className="text-xs sm:text-sm text-gray-600 mt-1">
                          Serves {servings} · ${price}
                        </div>
                      </label>
                    );
                  })}
                </div>
              </motion.div>
            )}
          </motion.div>
        )}

        {isCupcakeFlow && (
          <motion.div
            key="cupcake"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="bg-red-50 border border-candy-border rounded-xl p-4 sm:p-5"
          >
            <label className="block font-semibold text-gray-700 mb-2" htmlFor="cupcake-quantity-dozens">
              Cupcake quantity
            </label>
            <select
              id="cupcake-quantity-dozens"
              value={
                formData.cupcakeDozens !== '' &&
                formData.cupcakeDozens !== undefined &&
                isAllowedCupcakeDozens(Number(formData.cupcakeDozens))
                  ? String(Number(formData.cupcakeDozens))
                  : ''
              }
              onChange={(e) => {
                const val = e.target.value;
                const dozensNum = Number(val || 0);
                const cupcakeBase = dozensNum > 0 ? dozensNum * 30 : 0;
                setFormData((prev) => {
                  const syncedUnits = syncCupcakeUnitConfigs(
                    prev.cupcakeUnitConfigs,
                    dozensNum,
                    prev.cupcakeConfigMode || 'uniform'
                  );
                  return {
                    ...prev,
                    cakeShape: 'Cupcakes',
                    cakeSize: 'Cupcakes',
                    cupcakeDozens: val === '' ? '' : dozensNum,
                    cupcakeUnitConfigs: syncedUnits,
                    servings: val === '' ? '' : String(dozensNum * 12),
                    baseCakePrice: cupcakeBase,
                    price: cupcakeBase > 0 ? cupcakeBase.toFixed(2) : '',
                  };
                });
              }}
              className="w-full p-3 border border-candy-border rounded-md text-sm bg-white"
            >
              <option value="">Select quantity</option>
              {CUPCAKE_DOZEN_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} dozen
                </option>
              ))}
            </select>
            <p className="text-xs sm:text-sm text-gray-600 mt-2">
              1 through 10 dozen, including half dozens (1.5, 2.5, ...). $30 per dozen base price.
            </p>
            {cupcakeDozens >= 1 && (
              <p className="text-sm font-medium text-candy-primary mt-2">
                Total cupcakes: {Math.round(cupcakeDozens * 12)} | Base: ${(cupcakeDozens * 30).toFixed(2)}
              </p>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {error && (
        <div className="text-red-600 text-sm font-medium text-center">{error}</div>
      )}

      <div className="flex flex-col-reverse sm:flex-row sm:justify-between gap-3 pt-2">
        <button
          type="button"
          onClick={prevStep}
          className="w-full sm:w-auto bg-gray-300 text-gray-800 font-semibold px-6 py-2.5 rounded-md hover:bg-gray-400"
        >
          ← Back
        </button>
        <button
          type="button"
          onClick={() => {
            const d = Number(formData.cupcakeDozens || 0);
            const cupcakesValid = isCupcakeFlow ? isAllowedCupcakeDozens(d) : true;
            const cakeValid = !isCupcakeFlow && formData.cakeShape && formData.cakeSize;
            if (skipValidation && (cakeValid || cupcakesValid)) {
              setError('');
              nextStep();
              return;
            }
            if (isCupcakeFlow && cupcakesValid) {
              setError('');
              nextStep();
              return;
            }
            if (!isCupcakeFlow && cakeValid) {
              setError('');
              nextStep();
              return;
            }
            if (isCupcakeFlow) {
              setError('Please select cupcake quantity (1–10 dozen, half-dozen steps).');
            } else {
              setError('Please select both a cake shape and a size.');
            }
          }}
          className="w-full sm:w-auto bg-[#a62c2b] text-white font-semibold px-6 py-2.5 rounded-md hover:bg-[#8a1f1c]"
        >
          Next ➜
        </button>
      </div>
    </div>
  );
};

export default StepThreeCakeDetails;
